import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ArrowUpRight, Menu, X, Link2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileServicesClicked, setMobileServicesClicked] = useState(false);
  const [time, setTime] = useState(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }));
  const [showSplash, setShowSplash] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    const timer = setInterval(() => {
      setTime(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }));
    }, 1000);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    // Check if we're on the home page and it's a page reload
    if (location.pathname === '/' && performance.navigation.type === 1) {
      setShowSplash(true);
      const splashTimer = setTimeout(() => {
        setShowSplash(false);
      }, 2000);
      return () => clearTimeout(splashTimer);
    }
  }, [location.pathname]);

  const servicesData = [
    { title: "Website Design", route: "/services/web-design-development", links: ["Design", "Development", "E-Commerce", "Custom"] },
    { title: "UI/UX", route: "/services/ui-ux", links: ["User Research", "User Experience"] },
    { title: "Social Media / Branding", route: "/services/visual-branding", links: ["Strategy", "Visual Identity", "Social Media", "Custom"] },
    { title: "What We Use", route: "/services/what-we-use", links: ["Custom Code", "Low Code", "Analytics"] },
    { title: "SEO", route: "/services/seo", links: ["Local SEO", "On-Page SEO", "Custom"] },
    { title: "Consultation", route: "/services/consultation-audit", links: ["Business Audit", "Website Audit", "Custom"] }
  ];

  return (
    <>
      {/* SPLASH SCREEN */}
      <AnimatePresence>
        {showSplash && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-[200] bg-white flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="text-center"
            >
              <motion.img
                src="/logo.jpg"
                alt="Caffryx Logo"
                className="w-32 h-32 md:w-40 md:h-40 mx-auto mb-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              />
              <motion.h1
                className="text-3xl md:text-5xl font-black text-[#020617] mb-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                Caffryx
              </motion.h1>
              <motion.p
                className="text-lg md:text-xl font-medium text-[#4A5568]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
              >
                Connect. Consult. Create.
              </motion.p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    <div className={`fixed top-0 left-0 w-full z-[100] transition-all duration-500 ${scrolled ? 'pt-0' : 'pt-6'}`}>
      <div className={`mx-auto transition-all duration-500 ${scrolled ? 'max-w-full' : 'max-w-[95%]'}`}>
        
        <header className="flex items-stretch border-2 border-[#020617] bg-white shadow-[8px_8px_0px_0px_rgba(2,6,23,1)] relative z-[110]">
          
{/* --- LOGO & TITLE SECTION --- */}
          <a 
            href="https://www.caffryx.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-6 md:px-10 py-3 flex items-center gap-4 group transition-all relative border-r-2 border-[#020617]/5"
            onClick={() => setMobileMenuOpen(false)}
          >
            <div className="relative w-14 h-14 md:w-16 md:h-16 transition-all duration-500 group-hover:scale-110">
              <img 
                src="/logo.jpg" 
                alt="Caffryx Logo" 
                className="w-full h-full object-contain" 
              />
            </div>

            <div className="flex flex-col justify-center">
              <span className="text-2xl md:text-3xl font-black tracking-tighter text-[#020617] leading-none transition-colors group-hover:text-[#2B6CB0]">
                CAFFRYX
              </span>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2B6CB0] animate-pulse"></span>
                <span className="text-[8px] font-bold text-[#4A5568] uppercase tracking-[0.3em]">
                  Connect. Consult. Create.
                </span>
              </div>
            </div>
            
            {/* Subtle brand background on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#F0F4F8] to-transparent opacity-0 group-hover:opacity-100 transition-opacity -z-10"></div>
          </a>

          {/* MAIN NAV - DESKTOP */}
          <nav className="flex-grow flex items-center justify-end px-6 md:px-10 bg-white">
            <div className="hidden lg:flex items-center gap-8 mr-10">
              <NavLink label="Home" to="/" />
              
              <div className="relative h-full py-6 cursor-pointer" onMouseEnter={() => setIsServicesOpen(true)} onClick={() => navigate('/services')}>
                <button className={`flex items-center gap-2 px-3 py-1.5 rounded-md border-2 transition-all duration-300 ${isServicesOpen ? 'text-[#2B6CB0] border-[#2B6CB0] bg-[#2B6CB0]/10 shadow-lg' : 'text-[#4A5568] border-[#2B6CB0]/20 bg-[#2B6CB0]/5 hover:border-[#2B6CB0] hover:bg-[#2B6CB0]/10'}`}>
                  <span className={`w-2 h-2 rounded-full border border-[#4A5568] ${isServicesOpen ? 'bg-[#2B6CB0] border-[#2B6CB0] animate-pulse' : ''}`}></span>
                  <span className="text-[11px] font-black uppercase tracking-[0.2em]">Services</span>
                  <ChevronDown size={12} className={`transition-transform duration-300 ${isServicesOpen ? 'rotate-180 text-[#2B6CB0]' : 'text-[#4A5568]'}`} />
                </button>
              </div>

              <NavLink label="Work" to="/work" />
              <NavLink label="Insights" to="/blog" />
              <NavLink label="Contact Us" to="/contact" />
            </div>

            {/* ACTION MODULE */}
            <div className="flex items-center gap-6">
              <div className="hidden xl:flex flex-col items-end border-r-2 border-[#020617]/10 pr-6">
                <span className="text-[8px] font-black text-[#2B6CB0] uppercase tracking-widest leading-none mb-1">DETROIT_STATION</span>
                <span className="text-[10px] font-bold text-[#4A5568] tabular-nums uppercase leading-none">{time}</span>
              </div>
              
              <Link 
                to="/contact" 
                className="hidden sm:flex bg-[#2B6CB0] hover:bg-[#020617] text-white px-6 py-2.5 text-[10px] font-black uppercase tracking-widest transition-all items-center gap-2 border-2 border-[#2B6CB0] hover:border-[#020617]"
              >
                Start Build <ArrowUpRight size={14} />
              </Link>
              
              <button 
                className="lg:hidden p-2 text-[#020617]" 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </nav>
        </header>

        {/* DESKTOP MEGA MENU */}
        <div 
          className={`hidden lg:block absolute top-full left-0 right-0 bg-[#F0F4F8] border-2 border-[#020617] border-t-0 transition-all duration-500 ease-in-out overflow-hidden shadow-[12px_12px_0px_0px_rgba(2,6,23,0.15)] ${
            isServicesOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
          }`}
          onMouseLeave={() => setIsServicesOpen(false)}
        >
          <div className="max-w-[1440px] mx-auto px-12 py-16 grid grid-cols-3 gap-x-16 gap-y-12">
            {servicesData.map((col, idx) => (
              <div key={idx} className="group/item">
                <Link to={col.route} className="flex items-center gap-2 mb-4 group/title" onClick={() => {
                  setIsServicesOpen(false);
                  window.scrollTo(0, 0);
                }}>
                  <div className="flex items-center bg-[#020617] text-white px-2 py-1 rounded-sm transition-colors group-hover/title:bg-[#2B6CB0]">
                    <span className="text-[10px] font-black mr-2">0{idx + 1}</span>
                    <Link2 size={12} className="text-[#2B6CB0] group-hover/title:text-white" />
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-[0.3em] text-[#020617] group-hover/title:text-[#2B6CB0] transition-colors">
                    {col.title}
                  </span>
                </Link>
                <ul className="space-y-3 border-l-2 border-[#020617]/10 pl-5 ml-4">
                  {col.links.map((link, lIdx) => (
                    <li key={lIdx}>
                      <Link to={col.route} className="text-[13px] font-bold text-[#4A5568] hover:text-[#020617] transition-all block translate-x-0 hover:translate-x-1">
                        {link}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="bg-[#020617] p-3 text-center">
             <p className="text-[8px] font-black uppercase tracking-[0.8em] text-white/30">Caffryx // Secured Connection // Detroit Station</p>
          </div>
        </div>

        {/* MOBILE OVERLAY MENU */}
        <div className={`lg:hidden fixed inset-0 bg-[#020617] z-[105] transition-transform duration-500 ease-in-out ${mobileMenuOpen ? 'translate-y-0' : '-translate-y-full'}`}>
          <div className="pt-32 px-10 flex flex-col h-full overflow-y-auto">
            <div className="space-y-6 pb-20">
              {['Home', 'Services', 'Work', 'Insights', 'Contact Us'].map((item) => {
                const isServices = item === 'Services';

                return (
                  <div key={item} className="border-b border-white/5 pb-4">
                    {isServices ? (
                      <div>
                        <div className="flex items-center justify-between w-full">
                          <button
                            onClick={() => {
                              navigate('/services');
                              setMobileMenuOpen(false);
                              setMobileServicesOpen(false);
                            }}
                            className="text-4xl font-black text-white uppercase tracking-tighter hover:text-[#2B6CB0] transition-colors"
                          >
                            {item}
                          </button>
                          <div
                            onClick={() => {
                              setMobileServicesClicked(!mobileServicesClicked);
                              setMobileServicesOpen(!mobileServicesOpen);
                            }}
                            onMouseEnter={() => setMobileServicesOpen(true)}
                            className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 cursor-pointer hover:bg-[#2B6CB0] hover:border-[#2B6CB0] hover:shadow-lg hover:scale-110 ${mobileServicesOpen ? 'bg-[#2B6CB0] border-[#2B6CB0]' : 'bg-white/10 border-white/30'}`}
                          >
                            <ChevronDown className={`transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180 text-white' : 'text-white'}`} size={24} />
                          </div>
                        </div>

                        <div className={`overflow-hidden transition-all duration-500 ${mobileServicesOpen ? 'max-h-[800px] mt-6 opacity-100' : 'max-h-0 opacity-0'}`}>
                          <div className="grid gap-6 pl-4 border-l-2 border-[#2B6CB0]">
                            {servicesData.map((service, sIdx) => (
                              <div key={sIdx}>
                                <Link
                                  to={service.route}
                                  className="text-lg font-black text-[#2B6CB0] uppercase tracking-widest"
                                  onClick={() => {
                                    setMobileMenuOpen(false);
                                    setMobileServicesOpen(false);
                                  }}
                                >
                                  {service.title}
                                </Link>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                                  {service.links.slice(0, 3).map((link, lIdx) => (
                                    <span key={lIdx} className="text-[10px] font-bold text-[#4A5568] uppercase">
                                      {link}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={
                          item === 'Home' ? '/' :
                          item === 'Insights' ? '/blog' :
                          item === 'Contact Us' ? '/contact' :
                          `/${item.toLowerCase().replace(' ', '-')}`
                        }
                        className="block text-4xl font-black text-white uppercase tracking-tighter hover:text-[#2B6CB0]"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          window.scrollTo(0, 0);
                        }}
                      >
                        {item}
                      </Link>
                    )}
                  </div>
                );
              })}

              {/* START BUILD BUTTON */}
              <div className="pt-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-[#2B6CB0] text-white px-8 py-4 text-lg font-black uppercase tracking-widest hover:bg-[#020617] transition-all"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Start Build <ArrowUpRight size={20} />
                </Link>
              </div>

              {/* CONTACT INFO */}
              <div className="pt-8 space-y-4">
                <div className="flex items-center gap-3 text-white">
                  <span className="text-sm font-bold">📞</span>
                  <a href="tel:8326176250" className="text-lg font-black hover:text-[#2B6CB0] transition-colors">
                    832.617.6250
                  </a>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <span className="text-sm font-bold">✉️</span>
                  <a href="mailto:caffryx@gmail.com" className="text-lg font-black hover:text-[#2B6CB0] transition-colors">
                    caffryx@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
    </>
  );
};

const NavLink = ({ label, to }) => (
  <Link
    to={to}
    onClick={() => window.scrollTo(0, 0)}
    className="text-[11px] font-black uppercase tracking-[0.2em] text-[#4A5568] hover:text-[#020617] transition-colors relative group"
  >
    {label}
    <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#2B6CB0] transition-all group-hover:w-full"></span>
  </Link>
);

export default Header;